import React, { useEffect, useRef, useState } from "react";
import apiService from "../services/api";
import { TwoFactorSetupModal } from "../components/modals/TwoFactorSetupModal";
import { TwoFactorVerifyModal } from "../components/modals/TwoFactorVerifyModal";

interface GoogleCallbackScreenProps {
  onLoginSuccess: (token: string) => void;
  onLoginError: (error: string) => void;
}

export const GoogleCallbackScreen: React.FC<GoogleCallbackScreenProps> = ({
  onLoginSuccess,
  onLoginError,
}) => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [show2FAVerifyModal, setShow2FAVerifyModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string>("");
  const [googleSecret, setGoogleSecret] = useState<string>("");
  const [googleCode, setGoogleCode] = useState<string>("");
  const [redirectUri, setRedirectUri] = useState<string>("");
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Guard against multiple calls
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    const processCallback = async () => {
      try {
        // Get query parameters from URL
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");

        if (!code) {
          const errorParam = params.get("error");
          const errorDescription = params.get("error_description");
          throw new Error(
            errorDescription || errorParam || "No authorization code received"
          );
        }

        // Determine redirect URI (should match what was sent to google_login_init)
        const calculatedRedirectUri =
          process.env.REACT_APP_GOOGLE_REDIRECT_URI ||
          window.location.origin + "/";

        // Store code and redirect URI for potential 2FA retry
        setGoogleCode(code);
        setRedirectUri(calculatedRedirectUri);

        console.log("[GoogleCallback] Processing callback with code and redirect_uri:", calculatedRedirectUri);
        const response = await apiService.googleLogin(code, calculatedRedirectUri);

        if (response.authToken) {
          console.log("[GoogleCallback] Login successful, storing token");
          localStorage.setItem("token", response.authToken);
          onLoginSuccess(response.authToken);
        } else {
          throw new Error("No token received from login");
        }
      } catch (err: any) {
        // Check if this is a 2FA code required error (401 - user needs to verify 2FA code)
        if (err.response?.status === 401 && err.response?.data?.detail?.message === "2FA code is required") {
          const googleSecretFromError = err.response.data.detail.google_secret || "";
          console.log("[GoogleCallback] 2FA code required, google_secret:", googleSecretFromError ? "present" : "none");
          setGoogleSecret(googleSecretFromError);
          setShow2FAVerifyModal(true);
          setIsProcessing(false);
          return;
        }

        // Check if this is a 2FA setup required error (401 - user needs to setup 2FA)
        if (err.response?.status === 401 && err.response?.data?.detail?.message === "2FA setup is required") {
          const emailFromError = err.response.data.detail.email;
          const googleSecretFromError = err.response.data.detail.google_secret || "";
          console.log("[GoogleCallback] 2FA setup required for:", emailFromError, "google_secret:", googleSecretFromError ? "present" : "none");

          try {
            // Call setup_2fa API to get QR code
            const setup2FAResponse = await apiService.setup2FA(emailFromError);
            console.log("[GoogleCallback] Got 2FA setup data, qr_code length:", setup2FAResponse.qr_code?.length || 0);

            // Show the 2FA setup modal with QR code
            console.log("[GoogleCallback] Setting QR code and showing modal");
            setQrCodeData(setup2FAResponse.qr_code);
            setGoogleSecret(googleSecretFromError);
            setShow2FASetupModal(true);
            setIsProcessing(false);
            console.log("[GoogleCallback] Modal state set, show2FASetupModal=true");
            return;
          } catch (setupError: any) {
            console.error("[GoogleCallback] Failed to setup 2FA:", setupError);
            setError("Failed to initialize 2FA setup. Please try again.");
            onLoginError("Failed to initialize 2FA setup. Please try again.");
            setIsProcessing(false);
            return;
          }
        }

        // Google authenticated somebody who is not a user here -- almost always
        // the browser being signed into a personal Google account rather than a
        // work one. The address is the whole answer, so name it: without it the
        // person is told only that login failed, with nothing to act on.
        const wrongEmail = err.response?.data?.detail?.wrong_email;
        if (err.response?.status === 400 && wrongEmail) {
          const message =
            `User ${wrongEmail} is not in the system. ` +
            `You are signed in to Google with a personal account. ` +
            `Sign out of it, or open a private window, then sign in again with your work account.`;
          console.log("[GoogleCallback] Wrong Google account:", wrongEmail);
          setError(message);
          onLoginError(message);
          setIsProcessing(false);
          return;
        }

        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Google login failed";
        console.error("[GoogleCallback] Error:", {
          message: errorMessage,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          headers: err.response?.headers,
        });
        setError(errorMessage);
        onLoginError(errorMessage);
        setIsProcessing(false);
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle2FAEnable = async (token: string) => {
    console.log("[GoogleCallback] Enabling 2FA with token:", token, "google_secret:", googleSecret ? "present" : "none");
    try {
      // Call google_login again with 2FA token and google_secret
      const response = await apiService.googleLogin(googleCode, redirectUri, {
        twofa_code: token,
        google_secret: googleSecret,
      });

      // Store token and update auth state
      localStorage.setItem("token", response.authToken);
      setShow2FASetupModal(false);
      setGoogleSecret(""); // Clear google_secret

      // Call success callback
      onLoginSuccess(response.authToken);
    } catch (err: any) {
      console.error("[GoogleCallback] Failed to enable 2FA:", err);
      setError("Failed to enable 2FA. Please verify the token and try again.");
      setShow2FASetupModal(false);
    }
  };

  const handleSkip2FA = async () => {
    console.log("[GoogleCallback] Skipping 2FA setup with google_secret:", googleSecret ? "present" : "none");
    try {
      // Call google_login with skip_2fa flag and google_secret
      const response = await apiService.googleLogin(googleCode, redirectUri, {
        skip_2fa: true,
        google_secret: googleSecret,
      });

      // Store token and update auth state
      localStorage.setItem("token", response.authToken);
      setShow2FASetupModal(false);
      setGoogleSecret(""); // Clear google_secret

      // Call success callback to redirect to patient page
      onLoginSuccess(response.authToken);
    } catch (err: any) {
      console.error("[GoogleCallback] Failed to skip 2FA:", err);
      setError("Failed to complete login. Please try again.");
      setShow2FASetupModal(false);
    }
  };

  const handle2FAVerify = async (token: string) => {
    console.log("[GoogleCallback] Verifying 2FA code with google_secret:", googleSecret ? "present" : "none");
    try {
      // Call google_login with 2FA code and google_secret
      const response = await apiService.googleLogin(googleCode, redirectUri, {
        twofa_code: token,
        google_secret: googleSecret,
      });

      // Store token and update auth state
      localStorage.setItem("token", response.authToken);
      setShow2FAVerifyModal(false);
      setGoogleSecret(""); // Clear google_secret

      // Call success callback to redirect to patient page
      onLoginSuccess(response.authToken);
    } catch (err: any) {
      console.error("[GoogleCallback] Failed to verify 2FA code:", err);
      setError("Failed to verify 2FA code. Please try again.");
      setShow2FAVerifyModal(false);
    }
  };

  console.log("[GoogleCallback] Rendering, states:", { show2FASetupModal, show2FAVerifyModal, isProcessing, error: !!error });

  return (
    <>
      {show2FASetupModal && (
        <TwoFactorSetupModal
          isOpen={true}
          onClose={() => {
            console.log("[GoogleCallback] Closing 2FA setup modal");
            setShow2FASetupModal(false);
            onLoginError("2FA setup cancelled.");
          }}
          qrCode={qrCodeData}
          onEnable={handle2FAEnable}
          onSkip={handleSkip2FA}
        />
      )}
      {show2FAVerifyModal && (
        <TwoFactorVerifyModal
          isOpen={true}
          onClose={() => {
            console.log("[GoogleCallback] Closing 2FA verify modal");
            setShow2FAVerifyModal(false);
            onLoginError("2FA verification cancelled.");
          }}
          onVerify={handle2FAVerify}
        />
      )}
      {!show2FASetupModal && !show2FAVerifyModal && (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Completing Google login...</p>
              </>
            ) : error ? (
              <>
                <div className="text-red-600 text-lg font-semibold mb-4">
                  Login Failed
                </div>
                <p className="text-gray-600">{error}</p>
                <a
                  href="/"
                  className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Back to Login
                </a>
              </>
            ) : (
              <>
                <div className="text-green-600 text-lg font-semibold mb-4">
                  Login Successful
                </div>
                <p className="text-gray-600">Redirecting...</p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
